import { useState } from 'react';
import React from 'react';
import { ChevronDown, ChevronUp } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { ProgressBar } from '@/components/base/progress-indicators/progress-indicators';
import { Badge } from '@/components/base/badges/badges';
import type { WeekData } from '@/utils/week-utils';
import { formatWeekRange, calculateTargetPercentage, getConfirmedVsPending } from '@/utils/week-utils';
import { receiverTargetService } from '@/data/mock-receiver-targets';
import { useTranslation } from 'react-i18next';
import { cx } from '@/utils/cx';
import type { Container } from '@/types/container';

interface WeekListViewProps {
    weeks: WeekData[];
    receiverId: string;
    year: number;
    onEditTarget?: (weekNumber: number) => void;
}

export const WeekListView = ({ weeks, receiverId, year, onEditTarget }: WeekListViewProps) => {
    const { t } = useTranslation(['pages:calendar', 'common']);
    const [expandedWeeks, setExpandedWeeks] = useState<Set<number>>(new Set());

    const toggleWeek = (weekNumber: number) => {
        setExpandedWeeks(prev => {
            const next = new Set(prev);
            if (next.has(weekNumber)) {
                next.delete(weekNumber);
            } else {
                next.add(weekNumber);
            }
            return next;
        });
    };

    return (
        <div className="bg-primary rounded-lg border border-secondary_alt overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-secondary_alt bg-secondary">
                            <th className="px-4 py-3 text-left text-sm font-medium text-primary">
                                {t('listView.week')}
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-primary">
                                {t('listView.containerTarget')}
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-primary">
                                {t('listView.revenueTarget')}
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-primary">
                                {t('listView.containers')}
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-primary">
                                {t('listView.value')}
                            </th>
                            <th className="px-4 py-3 text-center text-sm font-medium text-primary">
                                {t('listView.actions')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {weeks.map((weekData) => {
                            const isExpanded = expandedWeeks.has(weekData.weekNumber);
                            const target = receiverTargetService.getTarget(receiverId, year, weekData.weekNumber);
                            const containerTarget = target?.containerTarget || 0;
                            const revenueTarget = target?.revenueTarget || 0;
                            const containerPercentage = calculateTargetPercentage(weekData.totalContainers, containerTarget);
                            const revenuePercentage = calculateTargetPercentage(weekData.totalValue.amount, revenueTarget);
                            const { confirmed, pending } = getConfirmedVsPending(weekData.containers);
                            const weekRange = formatWeekRange(weekData);

                            return (
                                <React.Fragment key={weekData.weekNumber}>
                                    <tr
                                        className={cx(
                                            "border-b border-secondary_alt hover:bg-secondary transition-colors",
                                            isExpanded && "bg-secondary"
                                        )}
                                    >
                                        <td className="px-4 py-3">
                                            <div className="flex flex-col">
                                                <span className="text-sm font-medium text-primary">
                                                    {t('weekNumber', { number: weekData.weekNumber })}
                                                </span>
                                                <span className="text-xs text-tertiary">
                                                    {weekRange}
                                                </span>
                                                {weekData.isCurrentWeek && (
                                                    <Badge color="brand" size="sm" className="mt-1 w-fit">
                                                        {t('currentWeek')}
                                                    </Badge>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            {containerTarget > 0 ? (
                                                <div className="space-y-1">
                                                    <div className="flex items-center justify-between text-xs">
                                                        <span className="text-tertiary">{weekData.totalContainers} / {containerTarget}</span>
                                                        <span className="text-primary font-medium">{containerPercentage}%</span>
                                                    </div>
                                                    <ProgressBar
                                                        value={containerPercentage}
                                                        progressClassName={containerPercentage >= 100 ? 'bg-success-primary' : 'bg-warning-primary'}
                                                    />
                                                </div>
                                            ) : (
                                                <span className="text-sm text-tertiary">-</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            {revenueTarget > 0 ? (
                                                <div className="space-y-1">
                                                    <div className="flex items-center justify-between text-xs">
                                                        <span className="text-tertiary">
                                                            ${weekData.totalValue.amount.toLocaleString()} / ${revenueTarget.toLocaleString()}
                                                        </span>
                                                        <span className="text-primary font-medium">{revenuePercentage}%</span>
                                                    </div>
                                                    <ProgressBar
                                                        value={revenuePercentage}
                                                        progressClassName={revenuePercentage >= 100 ? 'bg-success-primary' : 'bg-warning-primary'}
                                                    />
                                                </div>
                                            ) : (
                                                <span className="text-sm text-tertiary">-</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-col">
                                                <span className="text-sm font-medium text-primary">
                                                    {weekData.totalContainers}
                                                </span>
                                                {weekData.totalContainers > 0 && (
                                                    <span className="text-xs text-tertiary">
                                                        {confirmed} {t('confirmed')}, {pending} {t('pending')}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="text-sm font-medium text-primary">
                                                ${weekData.totalValue.amount.toLocaleString()}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center justify-center gap-2">
                                                <Button
                                                    color="secondary"
                                                    size="sm"
                                                    iconLeading={isExpanded ? ChevronUp : ChevronDown}
                                                    onClick={() => toggleWeek(weekData.weekNumber)}
                                                    disabled={weekData.totalContainers === 0}
                                                >
                                                    {isExpanded ? t('listView.collapse') : t('listView.expand')}
                                                </Button>
                                                <Button
                                                    color="secondary"
                                                    size="sm"
                                                    onClick={() => onEditTarget?.(weekData.weekNumber)}
                                                >
                                                    {t('editTarget')}
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                    {isExpanded && weekData.containers.length > 0 && (
                                        <tr>
                                            <td colSpan={6} className="px-4 py-4 bg-secondary">
                                                <div className="space-y-4">
                                                    <h4 className="text-sm font-semibold text-primary mb-3">
                                                        {t('listView.containersDetails', { count: weekData.containers.length })}
                                                    </h4>
                                                    <div className="overflow-x-auto">
                                                        <table className="w-full">
                                                            <thead>
                                                                <tr className="border-b border-secondary_alt">
                                                                    <th className="px-3 py-2 text-left text-xs font-medium text-tertiary">
                                                                        {t('listView.containerNumber')}
                                                                    </th>
                                                                    <th className="px-3 py-2 text-left text-xs font-medium text-tertiary">
                                                                        {t('listView.route')}
                                                                    </th>
                                                                    <th className="px-3 py-2 text-left text-xs font-medium text-tertiary">
                                                                        {t('listView.eta')}
                                                                    </th>
                                                                    <th className="px-3 py-2 text-left text-xs font-medium text-tertiary">
                                                                        {t('listView.status')}
                                                                    </th>
                                                                    <th className="px-3 py-2 text-left text-xs font-medium text-tertiary">
                                                                        {t('listView.confirmation')}
                                                                    </th>
                                                                    <th className="px-3 py-2 text-left text-xs font-medium text-tertiary">
                                                                        {t('listView.value')}
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {weekData.containers.map((container: Container) => (
                                                                    <tr
                                                                        key={container.id}
                                                                        className="border-b border-secondary_alt hover:bg-primary transition-colors"
                                                                    >
                                                                        <td className="px-3 py-2">
                                                                            <span className="text-sm text-primary font-medium">
                                                                                {container.containerNumber}
                                                                            </span>
                                                                        </td>
                                                                        <td className="px-3 py-2">
                                                                            <span className="text-sm text-secondary">
                                                                                {container.route.from} → {container.route.to}
                                                                            </span>
                                                                        </td>
                                                                        <td className="px-3 py-2">
                                                                            <span className="text-sm text-primary">
                                                                                {container.eta?.toLocaleDateString('it-IT')}
                                                                            </span>
                                                                        </td>
                                                                        <td className="px-3 py-2">
                                                                            <Badge
                                                                                color={
                                                                                    container.status === 'Delivered' ? 'success' :
                                                                                    container.status === 'Delayed' ? 'error' :
                                                                                    container.status === 'At Port' ? 'warning' :
                                                                                    'gray'
                                                                                }
                                                                                size="sm"
                                                                            >
                                                                                {container.status}
                                                                            </Badge>
                                                                        </td>
                                                                        <td className="px-3 py-2">
                                                                            <Badge
                                                                                color={container.isConfirmed !== false ? 'success' : 'warning'}
                                                                                size="sm"
                                                                            >
                                                                                {container.isConfirmed !== false ? t('confirmed') : t('pending')}
                                                                            </Badge>
                                                                        </td>
                                                                        <td className="px-3 py-2">
                                                                            <span className="text-sm text-primary">
                                                                                ${container.value.amount.toLocaleString()}
                                                                            </span>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

